import React, { useState, useEffect } from 'react';
import { Truck, CheckCircle, RefreshCw, AlertTriangle } from 'lucide-react';
import { authenticatedFetch } from './authenticatedApiHelper';

interface DeliveryRecord {
  id: string;
  merchant_tenant_id: string;
  courier_tenant_id: string;
  assigned_driver_user_id: string | null;
  backup_driver_user_id: string | null;
  status: string;
  customer_name: string;
  destination_address: string;
  value_amount: number;
  commission_fee_amount: number;
  estimated_eta_seconds: number;
  created_at_utc: string;
  delivered_at_utc: string | null;
}

interface ActiveDeliveryPanelProps {
  activeRole: string;
  tenantId: string;
  onAddLog: (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => void;
  onTriggerPOD: (deliveryId: string) => void;
  onTriggerTrackingView: (deliveryId: string) => void;
}

export const ActiveDeliveryPanel: React.FC<ActiveDeliveryPanelProps> = ({ 
  activeRole, 
  tenantId, 
  onAddLog, 
  onTriggerPOD,
  onTriggerTrackingView
}) => {
  const [deliveries, setDeliveries] = useState<DeliveryRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Reassignment form state
  const [activeReassignId, setActiveReassignId] = useState<string | null>(null);
  const [reassignDriverId, setReassignDriverId] = useState<string>('');
  const [reassignReason, setReassignReason] = useState<string>('Driver delay fallback activation');

  // Incident form state
  const [activeIncidentId, setActiveIncidentId] = useState<string | null>(null);
  const [incidentType, setIncidentType] = useState<string>('TRAFFIC_GRIDLOCK');
  const [incidentDesc, setIncidentDesc] = useState<string>('');

  // Mock telemetry status state
  const [telemetryTargetId, setTelemetryTargetId] = useState<string | null>(null);
  const [mockLat, setMockLat] = useState<string>('51.5074');
  const [mockLng, setMockLng] = useState<string>('-0.1278');

  const loadDeliveries = async () => {
    if (activeRole === 'guest') return;
    setLoading(true);
    setErrorMsg(null);
    try {
      const payload = await authenticatedFetch('/api/direct-delivery/deliveries');
      setDeliveries(payload.data || []);
    } catch (e: any) {
      setErrorMsg(e.message || 'Network error fetching deliveries.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDeliveries();
  }, [activeRole, tenantId]);

  const handleStateTransition = async (id: string, nextStatus: string) => {
    setErrorMsg(null);
    setSuccessMsg(null);

    if (nextStatus === 'delivered') {
      onTriggerPOD(id);
      return;
    }

    try {
      await authenticatedFetch(`/api/direct-delivery/deliveries/${id}/status`, {
        method: 'POST',
        body: { status: nextStatus }
      });

      setSuccessMsg(`Delivery successfully updated to: ${nextStatus.toUpperCase()}`);
      onAddLog(
        'Live Dispatch', 
        `Transition completed: delivery ${id} -> ${nextStatus}`, 
        'SUCCESS', 
        'State locked cleanly inside SQLite.'
      );
      await loadDeliveries();
    } catch (e: any) {
      if (e.statusCode === 409) {
        setErrorMsg(`[TRANSITION CONFLICT 409] Next status denied by business contract block: ${e.message}`);
        onAddLog('Live Dispatch', `Conflict 409: Status rejected.`, 'DENIED', e.message || 'Invalid transition.');
      } else {
        setErrorMsg(e.message || 'Failed to update courier delivery status.');
      }
    }
  };

  const handleOfferResponse = async (id: string, accept: boolean) => {
    setErrorMsg(null);
    setSuccessMsg(null);
    try {
      await authenticatedFetch(`/api/direct-delivery/deliveries/${id}/respond`, {
        method: 'POST',
        body: { accept }
      });
      setSuccessMsg(accept ? "Accepted delivery job! Ready to roll." : "Declined job offer.");
      onAddLog('Live Dispatch', `Offer response: ${accept ? 'ACCEPTED' : 'DECLINED'} for ${id}`, 'SUCCESS', 'SQL sync executed.');
      await loadDeliveries();
    } catch (e: any) {
      setErrorMsg(e.message || 'Failed to send offer response.');
    }
  };

  const handleRequestBackup = async (id: string) => {
    if (!window.confirm("Send standard distress signal for system automated backup reallocation?")) return;
    setErrorMsg(null);
    setSuccessMsg(null);
    try {
      await authenticatedFetch(`/api/direct-delivery/deliveries/${id}/request-backup`, {
        method: 'POST',
        body: { reason: "Estimated pickup SLA exceeding bounds" }
      });
      setSuccessMsg("Dispatched contingency triggers. Backup driver is authorized.");
      onAddLog('Emergency Cover', `Signalled priority re-drive on ${id}.`, 'WARNING', 'Backup invitation active.');
      await loadDeliveries();
    } catch (e: any) {
      setErrorMsg(e.message || 'Contingency fails.');
    }
  };

  const handleReassign = async (id: string) => {
    setErrorMsg(null);
    setSuccessMsg(null);
    if (!reassignDriverId) {
      setErrorMsg("Please enter a valid driver ID code.");
      return;
    }
    try {
      await authenticatedFetch(`/api/direct-delivery/deliveries/${id}/reassign`, {
        method: 'POST',
        body: {
          new_driver_user_id: reassignDriverId,
          reason: reassignReason
        }
      });
      setSuccessMsg(`Delivery reallocated perfectly to Driver: ${reassignDriverId}`);
      onAddLog('Platform Administration', `Reassigned job ${id} to ${reassignDriverId}`, 'SUCCESS', 'Old location streams killed.');
      setActiveReassignId(null);
      await loadDeliveries();
    } catch (e: any) {
      setErrorMsg(e.message || 'Reassignment rejected.');
    }
  };

  const handleReportIncident = async (id: string) => {
    setErrorMsg(null);
    setSuccessMsg(null);
    if (!incidentDesc) {
      setErrorMsg("A brief description of the transit incident is required.");
      return;
    }
    try {
      await authenticatedFetch(`/api/direct-delivery/deliveries/${id}/incidents`, {
        method: 'POST',
        body: {
          incident_type: incidentType,
          description: incidentDesc
        }
      });
      setSuccessMsg("Vetted incident logged onto dispatch dashboard ledger.");
      onAddLog('Secure Operations', `Logged ${incidentType} incident on job ${id}`, 'WARNING', incidentDesc);
      setActiveIncidentId(null);
      setIncidentDesc('');
      await handleStateTransition(id, 'delayed');
      await loadDeliveries();
    } catch (e: any) {
      setErrorMsg(e.message || 'Server rejected incident recording.');
    }
  };

  const submitMockLocation = async (id: string) => {
    setErrorMsg(null);
    setSuccessMsg(null);
    const parsedLat = parseFloat(mockLat);
    const parsedLng = parseFloat(mockLng);
    if (isNaN(parsedLat) || parsedLat < -90 || parsedLat > 90 || isNaN(parsedLng) || parsedLng < -180 || parsedLng > 180) {
      setErrorMsg("Invalid dynamic latitude/longitude coordinate range.");
      return;
    }

    try {
      await authenticatedFetch(`/api/direct-delivery/deliveries/${id}/location`, {
        method: 'POST',
        body: {
          latitude: parsedLat,
          longitude: parsedLng,
          accuracy_metres: 12.5,
          consent: true
        }
      });
      setSuccessMsg("Location coordinates processed successfully (Rate limit checked).");
      onAddLog('Live Telemetry', `Updated coordinates: ID ${id} (${parsedLat}, ${parsedLng})`, 'SUCCESS', 'Telemetry logs buffered.');
      setTelemetryTargetId(null);
    } catch (e: any) {
      if (e.statusCode === 429) {
        setErrorMsg("Too many location requests. Telemetry transmission rate limited to 1 update per 5s.");
      } else {
        setErrorMsg(e.message || 'Failed updating coordinates.');
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'created': return 'text-zinc-400 bg-zinc-900 border-zinc-800';
      case 'offered': return 'text-amber-400 bg-amber-500/5 border-amber-500/20';
      case 'accepted': return 'text-[#00ffff] bg-[#00ffff]/5 border-[#00ffff]/20';
      case 'travelling_to_pickup': return 'text-blue-400 bg-blue-500/5 border-blue-500/20';
      case 'arrived_at_pickup': return 'text-purple-400 bg-purple-500/5 border-purple-500/20';
      case 'collected': return 'text-indigo-400 bg-indigo-500/5 border-indigo-500/20';
      case 'delivering': return 'text-orange-400 bg-orange-500/5 border-orange-500/20';
      case 'delivered': return 'text-emerald-400 bg-emerald-500/5 border-emerald-500/20';
      case 'delayed': return 'text-rose-400 bg-rose-500/5 border-rose-500/20';
      case 'reassignment_requested': return 'text-amber-500 bg-amber-500/10 border-amber-500/30';
      default: return 'text-zinc-500 bg-zinc-900 border-zinc-800';
    }
  };

  const getPermittedTransitions = (status: string) => {
    switch (status) {
      case 'accepted': return [{ label: '🚀 Heading to Pickup', status: 'travelling_to_pickup' }];
      case 'travelling_to_pickup': return [{ label: '📍 Arrived at Merchant', status: 'arrived_at_pickup' }];
      case 'arrived_at_pickup': return [{ label: '🎒 Collected Bag', status: 'collected' }];
      case 'collected': return [{ label: '🚴 Out for Delivery', status: 'delivering' }];
      case 'delivering': return [{ label: '🏁 Mark Delivered (POD)', status: 'delivered' }];
      case 'delayed': return [
        { label: '♻️ Resume Progress', status: 'delivering' }
      ];
      default: return [];
    }
  };

  return (
    <div id="active-delivery-panel" className="bg-zinc-950/85 p-5 rounded-3xl border border-[#00ffff]/20 space-y-4 text-left">
      <div className="flex justify-between items-center border-b border-white/5 pb-2">
        <h3 className="text-xs font-black text-white uppercase tracking-wider flex items-center gap-1.5">
          <Truck className="w-4 h-4 text-[#00ffff]" />
          <span>Active Dispatch Constellation</span>
        </h3>
        <button 
          onClick={loadDeliveries}
          className="p-1 px-2.5 bg-zinc-900 border border-white/5 hover:bg-zinc-800 text-[9px] text-[#00ffff] font-mono rounded cursor-pointer flex items-center gap-1.5 uppercase shrink-0"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          <span>Sync Status</span>
        </button>
      </div>

      {errorMsg && (
        <div className="p-3.5 bg-rose-500/10 border border-rose-500/20 text-rose-400 text-[10px] font-mono rounded-xl flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{errorMsg}</span>
        </div>
      )}

      {successMsg && (
        <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] font-mono rounded-lg flex items-start gap-2">
          <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{successMsg}</span>
        </div>
      )}

      {deliveries.length === 0 ? (
        <div className="p-8 text-center text-zinc-500 border border-white/5 bg-black/20 rounded-2xl text-[10px] font-mono uppercase">
          No active delivery runs allocated currently. Use Dispatch Controls to schedule.
        </div>
      ) : (
        <div className="space-y-4 max-h-[500px] overflow-y-auto pr-1">
          {deliveries.map((job) => {
            const isDriverAssigned = job.assigned_driver_user_id === `user_${activeRole}`;
            const transitions = getPermittedTransitions(job.status);
            const isManagerOrAdmin = ['merchant', 'franchise', 'admin', 'super_admin'].includes(activeRole);

            return (
              <div key={job.id} className="p-4 bg-[#030612] border border-white/5 rounded-2xl space-y-3.5">
                <div className="flex justify-between items-start gap-2">
                  <div>
                    <span className="text-[12px] text-white font-bold block font-mono">Job {job.id}</span>
                    <p className="text-[8.5px] text-zinc-500 font-mono uppercase mt-0.5">
                      Dest: <strong className="text-zinc-300 font-sans">{job.destination_address}</strong> • Client: {job.customer_name}
                    </p>
                  </div>
                  <span className={`text-[8.5px] font-mono uppercase font-black px-2 py-0.5 border rounded-full ${getStatusColor(job.status)}`}>
                    ● {job.status}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-2 py-1.5 border-y border-white/3 font-mono text-[9px] text-zinc-400">
                  <div>
                    <span className="text-zinc-600 block text-[7.5px] uppercase">Cart Value</span>
                    <strong className="text-white text-[10.5px]">£{job.value_amount.toFixed(2)}</strong>
                  </div>
                  <div>
                    <span className="text-zinc-600 block text-[7.5px] uppercase">Client Net commission</span>
                    <strong className="text-[#00ffff] text-[10.5px]">£{job.commission_fee_amount.toFixed(2)}</strong>
                  </div>
                  <div>
                    <span className="text-zinc-600 block text-[7.5px] uppercase">Consolidated ETA</span>
                    <strong className="text-orange-400 text-[10.5px]">{Math.round(job.estimated_eta_seconds / 60)} Mins</strong>
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-2.5 pt-1.5 font-mono text-[9px]">
                  <div className="text-zinc-500 text-[8px] uppercase">
                    Driver: <span className="text-white">{job.assigned_driver_user_id || 'OPEN BID'}</span> 
                    {job.backup_driver_user_id && <span className="text-amber-400 block mt-0.5">⚠️ Reserve Cover: {job.backup_driver_user_id}</span>}
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    {/* CUSTOMER PERSPECTIVE TRACKING */}
                    <button
                      onClick={() => onTriggerTrackingView(job.id)}
                      className="px-2 py-1 bg-zinc-900 border border-white/5 hover:bg-zinc-800 text-white rounded text-[8px] cursor-pointer"
                    >
                      🔗 Client Map
                    </button>

                    {/* DRIVER OFFER RESPONSE ACTIONS */}
                    {job.status === 'offered' && isDriverAssigned && (
                      <>
                        <button
                          onClick={() => handleOfferResponse(job.id, true)}
                          className="px-2 py-1 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded cursor-pointer uppercase text-[8px]"
                        >
                          👍 Accept
                        </button>
                        <button
                          onClick={() => handleOfferResponse(job.id, false)}
                          className="px-2 py-1 bg-rose-600 hover:bg-rose-500 text-white font-bold rounded cursor-pointer uppercase text-[8px]"
                        >
                          👎 Reject
                        </button>
                      </>
                    )}

                    {/* DRIVER ROUTE STATE CONTROLS */}
                    {job.status !== 'delivered' && job.status !== 'cancelled' && job.status !== 'offered' && (isDriverAssigned || isManagerOrAdmin) && (
                      <>
                        {transitions.map((t) => (
                          <button
                            key={t.status}
                            onClick={() => handleStateTransition(job.id, t.status)}
                            className="px-2 py-1 bg-[#00ffff] hover:opacity-90 text-black font-black rounded cursor-pointer uppercase text-[8px]"
                          >
                            {t.label}
                          </button>
                        ))}

                        {/* DELAYED LOG */}
                        {job.status !== 'delayed' && (
                          <button
                            onClick={() => handleStateTransition(job.id, 'delayed')}
                            className="px-2 py-1 bg-rose-950/40 border border-rose-500/20 hover:bg-rose-900/40 text-rose-400 rounded text-[8px] cursor-pointer"
                          >
                            ⏱️ Traffic Blocked
                          </button>
                        )}

                        {/* TELEMETRY TRANSMITTER SIMULATOR CONTROLS */}
                        <button
                          onClick={() => setTelemetryTargetId(telemetryTargetId === job.id ? null : job.id)}
                          className="px-2 py-1 bg-purple-950/40 border border-purple-500/20 text-purple-300 rounded text-[8px] cursor-pointer"
                        >
                          🛰️ GPS Mock
                        </button>

                        {/* REQUEST EMERGENCY RESERVE COURIER */}
                        <button
                          onClick={() => handleRequestBackup(job.id)}
                          className="px-2 py-1 bg-yellow-950/40 border border-yellow-500/20 text-yellow-400 rounded text-[8px] cursor-pointer"
                        >
                          🚨 Request Cover
                        </button>
                      </>
                    )}

                    {/* SECURE INCIDENT LOGGING */}
                    {job.status !== 'delivered' && job.status !== 'cancelled' && (isDriverAssigned || isManagerOrAdmin) && (
                      <button
                        onClick={() => setActiveIncidentId(activeIncidentId === job.id ? null : job.id)}
                        className="px-2 py-1 bg-zinc-900 border border-white/5 text-rose-400 hover:bg-rose-500/5 rounded text-[8px] cursor-pointer"
                      >
                        ⚠️ Log Incident
                      </button>
                    )}

                    {/* MANAGER REASSIGNMENT ACTIONS */}
                    {isManagerOrAdmin && (
                      <button
                        onClick={() => setActiveReassignId(activeReassignId === job.id ? null : job.id)}
                        className="px-2 py-1 bg-[#00ffff]/10 border border-[#4be5e5]/20 text-[#00ffff] rounded text-[8px] cursor-pointer"
                      >
                        🔄 Override/Reassign
                      </button>
                    )}
                  </div>
                </div>

                {/* TELEMETRY POPUP */}
                {telemetryTargetId === job.id && (
                  <div className="p-3 bg-black/60 border border-purple-500/20 rounded-xl space-y-2.5 font-mono text-[9px]">
                    <span className="font-bold text-purple-300 block uppercase">🛰️ MOCK TRANSIT POSITION</span>
                    <p className="text-zinc-500 text-[8px] uppercase">
                      Periodically pushes location telemetry strings to the backend (Rate limit: max 1 per 5s).
                    </p>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <label className="text-[7.5px] text-zinc-500 block uppercase">Latitude</label>
                        <input 
                          type="text"
                          value={mockLat}
                          onChange={(e) => setMockLat(e.target.value)}
                          className="w-full bg-zinc-900 rounded border border-white/10 p-1 px-1.5 text-white"
                        />
                      </div>
                      <div className="flex-1">
                        <label className="text-[7.5px] text-zinc-500 block uppercase">Longitude</label>
                        <input 
                          type="text"
                          value={mockLng}
                          onChange={(e) => setMockLng(e.target.value)}
                          className="w-full bg-zinc-900 rounded border border-white/10 p-1 px-1.5 text-white"
                        />
                      </div>
                    </div>
                    <button
                      onClick={() => submitMockLocation(job.id)}
                      className="w-full py-1 bg-purple-600 text-white font-bold rounded hover:bg-purple-500 cursor-pointer"
                    >
                      📡 TELECAST TELEMETRY TO BACKEND
                    </button>
                  </div>
                )}

                {/* INCIDENT POPUP */}
                {activeIncidentId === job.id && (
                  <div className="p-3 bg-black/60 border border-rose-500/20 rounded-xl space-y-2.5 font-mono text-[9px]">
                    <span className="font-bold text-rose-400 block uppercase">🚨 RECORD TRANSIT DISRUPTION</span>
                    <div className="space-y-1.5">
                      <label className="text-[7.5px] text-zinc-500 block uppercase">Incident Classification</label>
                      <select 
                        value={incidentType}
                        onChange={(e) => setIncidentType(e.target.value)}
                        className="w-full bg-zinc-900 border border-white/10 rounded p-1.5 text-white"
                      >
                        <option value="TRAFFIC_GRIDLOCK">Severe Traffic Congestion / SLA Breach</option>
                        <option value="VEHICLE_BREAKDOWN">Vehicle Malfunction / Tyre Puncture</option>
                        <option value="ACCIDENT">Road Collision / Personal Injury</option>
                        <option value="CUSTOMER_NOT_FOUND">Customer Missing / Unreachable PIN</option>
                        <option value="MERCHANT_DELAY">Merchant Preparation Backlog</option>
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[7.5px] text-zinc-500 block uppercase">Description Notes</label>
                      <textarea
                        value={incidentDesc}
                        onChange={(e) => setIncidentDesc(e.target.value)}
                        placeholder="Provide details about the blockages..."
                        className="w-full h-12 bg-zinc-900 border border-white/10 rounded p-1.5 text-white outline-none resize-none"
                      />
                    </div>
                    <button
                      onClick={() => handleReportIncident(job.id)}
                      className="w-full py-1.5 bg-rose-600 text-white font-bold rounded hover:bg-rose-500 cursor-pointer uppercase tracking-wide text-[8.5px]"
                    >
                      ⚠️ SUMMIT AUDIT EVENT FOR ADMINISTRATIVE REVIEW
                    </button>
                  </div>
                )}

                {/* OVERRIDE/REASSIGN POPUP */}
                {activeReassignId === job.id && (
                  <div className="p-3 bg-black/60 border border-cyan-500/20 rounded-xl space-y-2.5 font-mono text-[9px]">
                    <span className="font-bold text-[#00ffff] block uppercase">🔄 FORCE REASSIGN COURIER</span>
                    <div className="space-y-1.5">
                      <label className="text-[7.5px] text-zinc-500 block uppercase">New Assignee User ID</label>
                      <input 
                        type="text"
                        placeholder="e.g. user_driver (or email)"
                        value={reassignDriverId}
                        onChange={(e) => setReassignDriverId(e.target.value)}
                        className="w-full bg-zinc-900 rounded border border-white/10 p-1.5 text-white outline-none focus:border-[#00ffff]"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[7.5px] text-zinc-500 block uppercase">Reassignment Justification reason</label>
                      <input 
                        type="text"
                        value={reassignReason}
                        onChange={(e) => setReassignReason(e.target.value)}
                        className="w-full bg-zinc-900 rounded border border-white/10 p-1.5 text-white outline-none"
                      />
                    </div>
                    <button
                      onClick={() => handleReassign(job.id)}
                      className="w-full py-1.5 bg-[#00ffff] text-black font-black rounded hover:opacity-90 cursor-pointer text-[8.5px] uppercase"
                    >
                      💫 OVERWRITE ASSIGNMENT RECORD NOW
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
